import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ordersAPI, paymentsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, SlidersHorizontal, X, ShoppingBag, ChevronRight, ChevronLeft } from 'lucide-react';
import { HelpTooltip } from '@/components/HelpTooltip';
import { EmptyState } from '@/components/shared/UXComponents';

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.25)' },
  confirmed: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB', border: 'rgba(37,99,235,0.25)' },
  fabric_ordered: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA', border: 'rgba(147,51,234,0.25)' },
  fabric_received: { bg: 'rgba(6,182,212,0.08)', text: '#0891B2', border: 'rgba(6,182,212,0.25)' },
  in_production: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  ready_for_trial: { bg: 'rgba(16,185,129,0.08)', text: '#059669', border: 'rgba(16,185,129,0.25)' },
  alterations_in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.25)' },
  ready_for_delivery: { bg: 'rgba(16,185,129,0.12)', text: '#047857', border: 'rgba(16,185,129,0.30)' },
  delivered: { bg: 'rgba(27,42,74,0.08)', text: '#1B2A4A', border: 'rgba(27,42,74,0.20)' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', border: 'rgba(239,68,68,0.25)' },
};

const STATUS_LABELS = {
  draft: 'Draft', confirmed: 'Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', in_production: 'In Production',
  ready_for_trial: 'Ready for Trial', alterations_in_progress: 'Alterations',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered', cancelled: 'Cancelled',
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}
      data-testid={`order-status-badge-${status}`}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

const CURRENCY_SYMBOLS = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };

export default function OrdersListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [overdueIds, setOverdueIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: (page - 1) * pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await ordersAPI.list(params);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [statusFilter, page]);

  useEffect(() => {
    paymentsAPI.overdueOrders().then(res => {
      setOverdueIds(new Set(res.data?.overdue_order_ids || []));
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
    setSearchParams({});
  };

  const totalPages = Math.ceil(total / pageSize);

  const hasFilters = search || statusFilter;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Orders</h1>
          <HelpTooltip text="All bespoke orders. Click + New Order to start, or click an order to manage its progress." testId="help-orders" />
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            {total} order{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <Link to="/app/orders/new" data-testid="new-order-button">
          <Button className="gap-2 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}>
            <Plus className="w-4 h-4" /> New Order
          </Button>
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order # or client name..."
            className="pl-9 h-10 rounded-lg"
            data-testid="orders-search-input"
          />
        </form>
        <Button variant="outline" size="sm" className="gap-1.5 h-10 rounded-lg"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="orders-filter-toggle">
          <SlidersHorizontal className="w-4 h-4" /> Filters
          {hasFilters && <span className="ml-1 w-2 h-2 rounded-full bg-[color:var(--brand-gold)]" />}
        </Button>
      </div>

      {showFilters && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="min-w-[180px]">
              <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--ink-muted)' }}>Status</p>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 rounded-lg" data-testid="orders-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="gap-1 text-xs mt-4" onClick={clearFilters}>
                <X className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="orders-table">
            <thead>
              <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                {['Order #', 'Client', 'Stylist', 'Garments', 'Status', 'Total', 'Required By', 'Date'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{h}</th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><Skeleton className="h-5 w-20" /></td>
                    ))}
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: 'var(--brand-navy)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                      {hasFilters ? 'No orders match your filters' : 'No orders yet'}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                      {hasFilters ? 'Try adjusting your search or filters' : 'Create your first order to get started'}
                    </p>
                    {!hasFilters && (
                      <Link to="/app/orders/new">
                        <Button size="sm" className="mt-4 gap-1.5 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}>
                          <Plus className="w-3.5 h-3.5" /> New Order
                        </Button>
                      </Link>
                    )}
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="border-b hover:bg-[color:var(--surface-2)] group cursor-pointer" style={{ borderColor: 'rgba(27,42,74,0.04)', transition: 'background-color 120ms' }}>
                    <td className="px-4 py-3">
                      <Link to={`/app/orders/${o.id}`} className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }} data-testid={`order-link-${o.id}`}>
                        {o.order_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/app/clients/${o.client_id}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--navy-ink)' }}>
                        {o.client_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--ink-muted)' }} data-testid={`order-stylist-${o.id}`}>
                      {o.stylist_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--navy-ink)' }}>
                      {o.garment_summary}
                      <span className="text-xs ml-1" style={{ color: 'var(--ink-muted)' }}>({o.item_count})</span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} />
                      {overdueIds.has(o.id) && (
                        <Badge className="ml-1.5 text-[9px] px-1.5 py-0 rounded-md" data-testid={`overdue-badge-${o.id}`}
                          style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#D97706', border: '1px solid rgba(245,158,11,0.3)' }}>
                          Balance Due
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                      {CURRENCY_SYMBOLS[o.currency] || ''}{o.total_price?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: o.required_by_date ? 'var(--navy-ink)' : 'var(--ink-muted)' }}>
                      {o.required_by_date || '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--ink-muted)' }}>
                      {o.order_date}
                    </td>
                    <td className="px-4 py-1">
                      <Link to={`/app/orders/${o.id}`}>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-50" style={{ transition: 'opacity 150ms' }} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 1}
                onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm"
                    className="h-8 w-8 p-0 text-xs" onClick={() => setPage(pageNum)}
                    style={page === pageNum ? { backgroundColor: 'var(--brand-navy)' } : {}}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && orders.length === 0 && (
          <EmptyState
            icon={ShoppingBag}
            title="No orders found"
            description={hasFilters ? "Try adjusting your filters" : "Create your first order to get started"}
            action={!hasFilters && (
              <Link to="/app/orders/new">
                <Button className="gap-2 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}>
                  <Plus className="w-4 h-4" /> New Order
                </Button>
              </Link>
            )}
          />
        )}
      </Card>
    </div>
  );
}
